// frontend/src/components/GoalsByMonth.js

import React, { useEffect, useMemo, useState } from "react";
import { CaretLeft, CaretRight, Plus, Trash, Star, X } from "phosphor-react";
import { api } from "../api";

function ymKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

const STORAGE_PREFIX = "goals:v1:";

export default function GoalsByMonth(props) {
  const { variant = "modal", open = true } = props;

  if (variant === "modal") {
    if (!open) return null;
    return <GoalsByMonthInner {...props} variant="modal" />;
  }

  return <GoalsByMonthInner {...props} variant="inline" />;
}

function GoalsByMonthInner({ className = "", variant = "modal", onClose }) {
  const [viewDate, setViewDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const monthLabel = useMemo(
    () => viewDate.toLocaleString("en-US", { month: "long", year: "numeric" }),
    [viewDate]
  );

  const monthOnly = useMemo(
    () => viewDate.toLocaleString("en-US", { month: "long" }),
    [viewDate]
  );

  const [items, setItems] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const loadFromAPI = async (y, m) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/goals/${y}/${m}`);
      const next = Array.isArray(data?.items) ? data.items : [];
      setItems(next);
      setDirty(false);

      try {
        localStorage.setItem(
          `${STORAGE_PREFIX}${y}-${String(m).padStart(2, "0")}`,
          JSON.stringify(next)
        );
      } catch {}
    } catch {
      try {
        const cached = localStorage.getItem(
          `${STORAGE_PREFIX}${y}-${String(m).padStart(2, "0")}`
        );
        setItems(cached ? JSON.parse(cached) : []);
      } catch {
        setItems([]);
      } finally {
        setDirty(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const y = viewDate.getFullYear();
    const m = viewDate.getMonth() + 1;
    loadFromAPI(y, m);
  }, [viewDate]);

  const addMonth = (delta) => {
    const d = new Date(viewDate);
    d.setMonth(d.getMonth() + delta);
    setViewDate(new Date(d.getFullYear(), d.getMonth(), 1));
  };

  const addItem = () => {
    setItems((p) => [...p, ""]);
    setDirty(true);
    setSaved(false);
  };

  const changeItem = (idx, val) => {
    setItems((p) => {
      const n = [...p];
      n[idx] = val;
      return n;
    });
    setDirty(true);
    setSaved(false);
  };

  const removeItem = (idx) => {
    setItems((p) => p.filter((_, i) => i !== idx));
    setDirty(true);
    setSaved(false);
  };

  const save = async () => {
    const cleaned = items.map((s) => (s ?? "").trim()).filter(Boolean);
    const y = viewDate.getFullYear();
    const m = viewDate.getMonth() + 1;

    setSaving(true);

    try {
      await api.put(`/goals/${y}/${m}`, { year: y, month: m, items: cleaned });
      setItems(cleaned);
      setDirty(false);
      setSaved(true);

      try {
        localStorage.setItem(
          `${STORAGE_PREFIX}${ymKey(viewDate)}`,
          JSON.stringify(cleaned)
        );
      } catch {}
    } catch {
      try {
        localStorage.setItem(
          `${STORAGE_PREFIX}${ymKey(viewDate)}`,
          JSON.stringify(cleaned)
        );
      } catch {}

      setItems(cleaned);
      setDirty(false);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  const isModal = variant === "modal";

  const wrapperClass = isModal
    ? "fixed inset-0 z-[150] grid place-items-center bg-black/35 overflow-y-auto md:p-6"
    : "";

  const cardClass = [
    `
      relative w-full overflow-hidden
      rounded-[28px] border border-blue-100/80
      sm:bg-white text-blue-950 sm:shadow-[0_8px_0px_rgba(37,99,235,0.96)]
    `,
    isModal ? "max-w-xl mx-auto p-4 md:p-6" : "px-4 py-3",
    className,
  ].join(" ");

  return (
    <div className={wrapperClass}>
      <div className={cardClass}>
        {isModal && (
          <button
            type="button"
            onClick={onClose}
            className="
              absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center
              rounded-full text-gray-800"
            aria-label="Close goals"
          >
            <X size={19} weight="bold" />
          </button>
        )}

        <div className="text-center">
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={() => addMonth(-1)}
              className="grid !min-h-0 !min-w-0 !h-9 !w-9 place-items-center rounded-full text-blue-400 transition hover:bg-white"
              aria-label="Previous month"
              title="Previous month"
            >
              <CaretLeft
                size={23}
                weight="fill"
                className="text-blue-400"
              />
            </button>

            <h2 className="text-blue-600 font-black leading-none tracking-[-0.01em] text-[20px] sm:text-[24px]">
              {monthOnly}
            </h2>

            <button
              type="button"
              onClick={() => addMonth(1)}
              className="grid !min-h-0 !min-w-0 !h-9 !w-9 place-items-center rounded-full text-blue-400 transition hover:bg-white"
              aria-label="Next month"
              title="Next month"
            >
              <CaretRight
                size={23}
                weight="fill"
                className="text-blue-400 sm:!w-[25px] sm:!h-[25px]"
              />
            </button>
          </div>
        </div>

        <div className="mt-2 sm:mt-5">
          <div className="rounded-[22px] sm:bg-blue-50 border sm:border-none border-blue-100/80 bg-white/90 p-3 md:p-4]">
            {loading ? (
              <div className="rounded-2xl bg-blue-50/60 px-3 py-3 text-sm font-semibold text-blue-500">
                Loading…
              </div>
            ) : (
              <>
                {items.length === 0 && (
                  <div className="mb-3 rounded-2xl bg-blue-50/60 px-4 pt-3 py-3 sm:pt-3 sm:pb-1">
                    <div className="text-md font-medium text-blue-600">
                      Set the tone for {monthOnly}
                    </div>
                    <div className="text-sm leading-5 text-gray-700">
                      Add a few clear goals - habits, milestones, or things you
                      want to make time for
                    </div>
                  </div>
                )}

                <ul className="space-y-2 sm:space-y-3">
                  {items.map((val, i) => (
                    <li
                      key={i}
                      className="
                        group flex items-center gap-1 rounded-2xl
                        border-2 border-blue-500 bg-blue-50 sm:bg-white
                        !px-2 !py-1 !min-h-0 !min-w-0 transition
                        !focus-within:sm:border-blue-600
                      "
                    >
                      <div className="grid !min-h-0 !min-w-0 !h-6 !w-6 shrink-0 place-items-center text-blue-600">
                        <Star weight="fill" size={15} />
                      </div>

                      <input
                        value={val}
                        onChange={(e) => changeItem(i, e.target.value)}
                        placeholder="Practice guitar 3 times a week"
                        className="
                          flex-1 bg-transparent text-sm text-black
                          outline-none placeholder:text-gray-400 
                        "
                      />

                      <button
                        type="button"
                        onClick={() => removeItem(i)}
                        className="
                          grid !min-h-0 !min-w-0 !h-6 !w-6 shrink-0 place-items-center rounded-full
                          text-gray-500 transition hover:bg-white hover:text-blue-600
                        "
                        aria-label="Remove"
                        title="Remove"
                      >
                        <Trash size={15} />
                      </button>
                    </li>
                  ))}
                </ul>

                <div className="mt-3 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={addItem}
                    className="
                      !inline-flex !items-center !min-h-0 !py-1 !gap-1 !rounded-full
                      !border !border-blue-100 !bg-blue-500 !px-3 
                      !text-sm !font-semibold !text-white
                      !transition !hover:bg-blue-50 ml-1 sm:ml-2
                    "
                  >
                    <Plus size={12} weight="bold" />
                    Add Goal
                  </button>

                  <button
                    type="button"
                    onClick={save}
                    disabled={loading || saving || !dirty}
                    className={`!rounded-full !px-3 !min-h-0 !min-w-0 !py-1 !text-sm !font-medium !transition ${
                      !loading && !saving && dirty
                        ? "!bg-blue-600 !text-white !hover:bg-blue-700"
                        : "!bg-blue-100 !text-blue-300 !cursor-default"
                    }`}
                  >
                    {saving ? "Saving…" : saved ? "Saved" : "Save"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}